/**
 * Displays a list of labels, optionally including asset tags.
 * Asset tags are loaded if the assetType and assetId props are provided.
 * The asset tag use case is currently applied in the device details view but this component is used in many places including in table column renderers.
 * Since asset tags are fetched async, this would require extra consideration if the desire is to show asset tags in those types of cases.
 */
import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';

import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import { Box, Flex, Popover, Text } from 'core/components';
import { Label } from 'app/components/labels/Label';
import { PopoverInteractionKind } from '@blueprintjs/core';

// not the first, not the last, and an even number
function lineBreakCheck(i, last) {
  return i !== 0 && i !== last && i % 2 === 1 && <br />;
}

const getLabelId = (label) => {
  if (label.isAssetTag) {
    return label.tag_name;
  }

  return typeof label.get === 'function' ? (label.get('id') ?? label.get('value')) : (label.id ?? label.value);
};

const LabelListWrapper = styled(Flex)`
  flex-direction: ${(props) => (props.isHorizontal === true ? 'row' : 'column')};
  gap: 4px;
  padding: ${(props) => (props.isHorizontal ? '4px 4px 4px 0' : '4px')};
  align-items: ${(props) => props.$itemsAlign ?? 'flex-start'};
  flex-wrap: ${(props) => (props.wrapLabels ? 'wrap' : 'nowrap')};
`;

/**
 * Displays a list of Labels
 *
 * @param labels labels to display
 * @param wrapLabels wrap labels using standard flex-row rules.
 * @param onRemove adds X button allowing user to remove labels
 * @param priorityLabels labels to prioritize displaying before all others. Mostly relevant when used with
 *                       `model.collection.discreteFilterValues` in order to show user-relevant labels before others.
 * @param shareLevel displays special Kentik labels for Kentik presets
 * @param fixedWidth if true, each label will have a fixed max width, otherwise labels will be sized to fit their content.
 * @param maxLabelsToShow maximum number of labels to display. Shows an indication of how many labels remain.
 * @param renderEmpty renders the label list (i.e. the container box) even if there are no labels to display. Beware of triggering errors with this.
 * @param endElement element to render at end of label list
 * @param isHorizontal if true, labels are displayed in a horizontal row, otherwise they are displayed in a vertical column
 * @param wrapperAlignItems sets the align-items CSS property on the label list wrapper.
 * @param labelMaxWidth maximum width of each label. If not provided, defaults to 250px.
 * @param rest remaining props (mostly expected to be styling) are transferred to the wrapper box
 * @returns {React.JSX.Element|null}
 */
const LabelList = ({
  $assetTags,
  labels: propLabels = [],
  wrapLabels,
  onRemove = null,
  priorityLabels = [],
  shareLevel,
  fixedWidth,
  maxLabelsToShow = 2,
  renderEmpty = false,
  endElement = null,
  isHorizontal = true,
  wrapperAlignItems,
  labelMaxWidth,
  labelDecorator = (_, labelComponent) => labelComponent,
  assetType,
  assetId,
  ...rest
}) => {
  const assetIdKey = `${assetId}`;
  const [assetTags, setAssetTags] = useState([]);
  const labels = [
    ...(Array.isArray(propLabels) ? propLabels : []),
    ...assetTags.map((tag) => ({ isAssetTag: true, ...tag }))
  ];

  useEffect(() => {
    setAssetTags([]);

    if (!assetType || !assetIdKey) {
      return;
    }

    const fetchRequest = makeCancelable($assetTags.fetchInventoryItemAssetTags({ assetType, assetIds: [assetIdKey] }));

    fetchRequest.promise
      .then((response) => setAssetTags(response))
      .catch((error) => {
        if (!(error instanceof CanceledError)) {
          console.error('Failed to fetch asset tags', error);
        }
      });

    // eslint-disable-next-line consistent-return
    return () => {
      fetchRequest.cancel();
    };
  }, [$assetTags, assetType, assetIdKey]);

  if (!renderEmpty && labels.length === 0) {
    return null;
  }

  const sortLabels = (a, b) => {
    const idA = getLabelId(a);
    const idB = getLabelId(b);
    // Priority labels come first
    if (priorityLabels.includes(idA)) {
      if (!priorityLabels.includes(idB)) {
        return -1;
      }
    } else if (priorityLabels.includes(idB)) {
      return 1;
    }
    // Then use numeric sort if appropriate
    if (typeof idA === 'number' && typeof idB === 'number') {
      return idA - idB;
    }
    // Otherwise simple string sort
    return String.prototype.localeCompare.call(idA, idB);
  };

  const getStyleOverrides = (hasFixedWidth, widthPercent = 50) =>
    hasFixedWidth ? { maxWidth: `calc(${widthPercent}% - 4px)` } : {};

  const getMoreCount = () => (labels.length ? labels.length - maxLabelsToShow : 0);

  const renderSingleLabel = (label, index, styleOverrides, isFullList = false) => {
    const maxWidth = isFullList ? 500 : labelMaxWidth || 250;

    if (label.isAssetTag) {
      const tag = `${label.tag_name}:${label.value}`;
      return <Label key={`assetTag-${tag}`} title={tag} name={tag} maxWidth={maxWidth} />;
    }

    const labelHasGet = typeof label.get === 'function';
    // If a `bg` is provided, use it, otherwise `color` is the bg.
    const bg = labelHasGet ? label.get('bg') : label.bg;
    const color = labelHasGet ? label.get('color') : label.color;
    const labelId = getLabelId(label);
    const testId = `labelId_${labelId}`;
    // display label name if available, otherwise label.label
    const name = labelHasGet ? label.get('name') || label.get('label') : label.name || label.label;

    const onRemoveFn = onRemove && ((event) => onRemove(labelId, event));
    return (
      <React.Fragment key={`${labelId}-${index}`}>
        {labelDecorator(
          labelId,
          <Flex {...styleOverrides} data-testid="labelWrapper">
            <Label
              bg={bg || color}
              color={bg ? color : undefined}
              name={name}
              onRemove={onRemoveFn}
              shareLevel={shareLevel}
              testId={testId}
              maxWidth={maxWidth}
            />
            {fixedWidth && lineBreakCheck(index, labels.length - 1)}
          </Flex>
        )}
      </React.Fragment>
    );
  };

  const renderFullList = (isPopover) =>
    labels.sort(sortLabels).map((label, i) => renderSingleLabel(label, i, getStyleOverrides(false), isPopover));

  const renderAbbreviatedList = () => {
    const itemWidth =
      getMoreCount() > 0
        ? 100 / (maxLabelsToShow + 1) // +1 to account for the "+x more" text
        : 100 / labels.length;

    return labels.sort(sortLabels).map((label, i) => {
      if (i >= maxLabelsToShow) {
        return null;
      }
      return renderSingleLabel(label, i, getStyleOverrides(fixedWidth, itemWidth));
    });
  };

  const renderMoreIndicator = () =>
    maxLabelsToShow && labels.length > maxLabelsToShow ? (
      <Flex key="moreIndicator">
        <Text mt="2px">+{labels.length - maxLabelsToShow} more</Text>
      </Flex>
    ) : (
      <></>
    );

  const renderEndElement = () => (endElement ? <span key="endElement">{endElement}</span> : null);

  const renderPopoverContent = () => (
    <LabelListWrapper isHorizontal={false} wrapLabels={wrapLabels}>
      {renderFullList(true)}
    </LabelListWrapper>
  );

  const renderContent = () => {
    const moreCount = getMoreCount();

    if (wrapLabels) {
      Object.assign(rest, { whiteSpace: 'normal' });
    }

    return moreCount > 0 ? (
      <Popover content={renderPopoverContent()} interactionKind={PopoverInteractionKind.HOVER}>
        <Box {...rest}>
          <LabelListWrapper wrapLabels={wrapLabels} isHorizontal={isHorizontal} $itemsAlign={wrapperAlignItems}>
            {renderAbbreviatedList()}
            {renderMoreIndicator()}
            {renderEndElement()}
          </LabelListWrapper>
        </Box>
      </Popover>
    ) : (
      <Box {...rest} width="100%">
        <LabelListWrapper wrapLabels={wrapLabels} isHorizontal={isHorizontal} $itemsAlign={wrapperAlignItems}>
          {renderFullList(false)}
          {renderEndElement()}
        </LabelListWrapper>
      </Box>
    );
  };

  return renderContent();
};

export default inject('$assetTags')(observer(LabelList));
